import {
  ArrowLeftOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useMemo } from 'react';
import styled from 'styled-components';

import { Flex, Space, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import IconButton from '@/components/Button/IconButton';
import ToggleRadio from '@/components/Common/ToggleRadio';
import AnimateText from '@/components/Typography/AnimatedText';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';

type Props = {
  dataAnonymization: boolean;
  setDataAnonymization: (status: boolean) => void;
  undo: () => void;
  onComplete: () => void;
};

const DataAnonymizeSetting = (props: Props) => {
  const color = useColor();
  return (
    <Style>
      <Flex a="center" g={12}>
        <ToggleRadio
          status={props.dataAnonymization}
          onChange={(s) => props.setDataAnonymization(s)}
        />
        <Flex g={4}>
          <Typo
            type="label2"
            color={props.dataAnonymization ? color.gray500 : color.gray800}
          >
            Off
          </Typo>
          <Typo type="label2" color={color.gray500}>
            /
          </Typo>
          <Typo
            type="label2"
            color={props.dataAnonymization ? color.gray800 : color.gray500}
          >
            On
          </Typo>
        </Flex>
      </Flex>
      <Typo type="label1"></Typo>
      <Space h={24} />
      <SampleCard dataAnonymization={props.dataAnonymization} />
      <Space h={24} />
      <Typo type="body2" color={color.gray600}>
        Syncly uses data masking techniques: replacing identifiable information
        with descriptions so reader can understand the context. This includes
        sensitive details such as names, addresses, phone numbers, and social
        security numbers. You can turn on/off this option anytime at your
        workspace settings.
      </Typo>
      <Space h={24} />
      <Typo type="body2" color={color.red600}>
        <ExclamationCircleOutlined />
        &nbsp; Once the data is imported, the setting of the specific data
        cannot be undone.
      </Typo>
      <Space h={6} />
      <Typo type="body2" color={color.red600}>
        <ExclamationCircleOutlined />
        &nbsp; Anonymization may potentially slow down the speed of importing
        feedback.
      </Typo>
      <Space h={32} />
      <Flex j="space-between" a="center">
        <IconButton
          icon={<ArrowLeftOutlined />}
          sizeType="s"
          outlined
          style={{
            width: '40px',
            height: '40px',
            fontSize: '18px',
            color: color.primary100,
          }}
          onClick={props.undo}
        />
        <Button
          variant="primary"
          sizeType="l"
          style={{ width: 200 }}
          onClick={props.onComplete}
        >
          Create workspace
        </Button>
      </Flex>
    </Style>
  );
};

const Style = styled.div``;

const SampleCard = (props: { dataAnonymization: boolean }) => {
  const color = useColor();
  const maskStyle = useMemo(() => {
    if (props.dataAnonymization)
      return {
        backgroundColor: color.gray300,
        padding: '2px 4px',
      };
    return { padding: '2px 4px' };
  }, [color.gray300, props.dataAnonymization]);

  return (
    <SampleCardStyle>
      <Flex className="avatar-box"></Flex>
      <VFlex>
        <Space h={12} />
        <Flex a="center" g={12}>
          <Typo type="label1" weight="b" color={color.gray700}>
            CUSTOMER
          </Typo>
          <Typo type="label1" weight="r" color={color.gray600}>
            10:12PM
          </Typo>
        </Flex>
        <Space h={12} />
        <Flex g={6}>
          <Typo type="label1" color={color.gray700}>
            The phone number is
          </Typo>
          <AnimateText type="label1" color={color.gray700} style={maskStyle}>
            {props.dataAnonymization ? '[PHONE NUMBER]' : '+1) 100-000-0000'}
          </AnimateText>
        </Flex>
        <Space h={8} />
        <Flex g={6}>
          <Typo type="label1" color={color.gray700}>
            Email address is
          </Typo>
          <AnimateText type="label1" color={color.gray700} style={maskStyle}>
            {props.dataAnonymization
              ? '[EMAIL ADDRESS]'
              : 'customer@google.com'}
          </AnimateText>
        </Flex>
      </VFlex>
    </SampleCardStyle>
  );
};

const SampleCardStyle = styled(Flex)`
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.gray200};
  padding: 16px;
  gap: 16px;
  .avatar-box {
    width: 56px;
    height: 56px;
    border-radius: 8px;
    background-color: ${(props) => props.theme.colors.yellow500};
  }
`;
export default DataAnonymizeSetting;
